import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { logger } from "../lib/logger";
import { useI18n } from "../i18n";
import { toast } from "sonner";
import BracketView from "../components/BracketView";
import {
    Loader2, Copy, Share2, Trophy, Calendar, Users, ListChecks,
    Settings, Award, Table2, Wallet, Sparkles, Crown,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

import FixturesView from "./pool/FixturesView";
import GroupsView from "./pool/GroupsView";
import MyPredictions from "./pool/MyPredictions";
import Leaderboard from "./pool/Leaderboard";
import BonusTab from "./pool/BonusTab";
import BetAndMembersCard from "./pool/BetAndMembersCard";
import AdminPanel from "./pool/AdminPanel";
import MvpHistory from "./pool/MvpHistory";
import { WelcomeBetModal, BetEditModal, DeletePoolModal, RemoveMemberModal } from "./pool/PoolModals";
import { isLocked } from "./pool/utils";

const TABS = [
    { id: "fixtures", labelKey: "pool.tab.fixtures", icon: Calendar },
    { id: "groups", labelKey: "pool.tab.groups", icon: Table2 },
    { id: "bracket", labelKey: "pool.tab.bracket", icon: Award },
    { id: "predictions", labelKey: "pool.tab.predictions", icon: ListChecks },
    { id: "bonus", labelKey: "pool.tab.bonus", icon: Sparkles },
    { id: "leaderboard", labelKey: "pool.tab.leaderboard", icon: Trophy },
    { id: "mvp", labelKey: "pool.tab.mvp", icon: Crown },
    { id: "bet", labelKey: "pool.tab.bet", icon: Wallet },
    { id: "admin", labelKey: "pool.tab.admin", icon: Settings, adminOnly: true },
];

export default function PoolDetail() {
    const { poolId } = useParams();
    const nav = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const { t } = useI18n();
    const [pool, setPool] = useState(null);
    const [matches, setMatches] = useState([]);
    const [teams, setTeams] = useState({});
    const [predictions, setPredictions] = useState({});
    const [leaderboard, setLeaderboard] = useState([]);
    const [insights, setInsights] = useState(null);
    const [standings, setStandings] = useState([]);
    const [predictedBracket, setPredictedBracket] = useState({});
    // Initial tab selection: ?tab=... in URL > location.state > default 'fixtures'.
    // Reading from URL means clicking Back from a match-detail page restores
    // the same tab the user was on (browser history preserves the search string).
    const [poolTab, setPoolTab] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const fromUrl = params.get("tab");
        if (fromUrl && TABS.some((t) => t.id === fromUrl)) return fromUrl;
        return "fixtures";
    });
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all"); // all | upcoming | locked
    const [search, setSearch] = useState("");
    const [showBetEdit, setShowBetEdit] = useState(false);
    const [showWelcomeBet, setShowWelcomeBet] = useState(false);
    const [betForm, setBetForm] = useState({ bet_amount: "", bet_currency: "", bet_winner: "", bet_instructions: "" });
    const [savingBet, setSavingBet] = useState(false);
    const [togglingPaid, setTogglingPaid] = useState({});
    const [showDeletePool, setShowDeletePool] = useState(false);
    const [deletingPool, setDeletingPool] = useState(false);
    const [removeMemberCandidate, setRemoveMemberCandidate] = useState(null); // { id, name }
    const [removingMember, setRemovingMember] = useState(false);
    // List of all pools the user is a member of — used to render the
    // quick-switch dropdown above the invite code when count > 1.
    const [myPools, setMyPools] = useState([]);

    const loadAll = useCallback(async () => {
        setLoading(true);
        try {
            const [poolRes, matchesRes, teamsRes, predsRes, minePools] = await Promise.all([
                api.get(`/pools/${poolId}`),
                api.get(`/matches`),
                api.get(`/teams`),
                api.get(`/pools/${poolId}/predictions/mine`),
                api.get(`/pools/mine`),
            ]);
            setPool(poolRes.data);
            setMatches(matchesRes.data);
            const tmap = {};
            teamsRes.data.forEach((tm) => { tmap[tm.id] = tm; });
            setTeams(tmap);
            const pmap = {};
            predsRes.data.forEach((p) => { pmap[p.match_id] = p; });
            setPredictions(pmap);
            setMyPools(minePools.data || []);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setLoading(false); }
    }, [poolId]);

    const loadLeaderboard = useCallback(async () => {
        try {
            const [lb, ins] = await Promise.all([
                api.get(`/pools/${poolId}/leaderboard`),
                api.get(`/pools/${poolId}/insights`),
            ]);
            setLeaderboard(lb.data);
            setInsights(ins.data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    }, [poolId]);

    const loadStandings = useCallback(async () => {
        try {
            const { data } = await api.get(`/standings`);
            setStandings(data);
        } catch (e) {
            logger.warn("standings failed", e);
        }
    }, []);

    const loadPredictedBracket = useCallback(async () => {
        try {
            const { data } = await api.get(`/pools/${poolId}/predicted-bracket`);
            setPredictedBracket(data || {});
        } catch (e) {
            logger.warn("predicted-bracket failed", e);
        }
    }, [poolId]);

    useEffect(() => { loadAll(); }, [loadAll]);
    useEffect(() => { if (poolTab === "leaderboard") loadLeaderboard(); }, [poolTab, loadLeaderboard]);
    // Switch to the tab requested via location.state (e.g. when a header
    // status pill links to /pools/{id} with state={tab:"bonus"} while we are
    // already on the pool page — react-router reuses this component so we
    // have to watch location.state explicitly).
    useEffect(() => {
        const requested = location.state?.tab;
        if (requested && TABS.some((tab) => tab.id === requested)) {
            setPoolTab(requested);
        }
    }, [location.state]);
    // Keep ?tab= in URL in sync with active tab so back-nav restores it.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get("tab") === poolTab) return;
        params.set("tab", poolTab);
        nav(`${window.location.pathname}?${params.toString()}`, { replace: true });
    }, [poolTab, nav]);
    // Standings are tiny + cached — fetch once on mount so the fixtures-tab
    // tooltip works without a tab switch.
    useEffect(() => { loadStandings(); }, [loadStandings]);
    useEffect(() => { if (poolTab === "bracket" || poolTab === "fixtures") loadPredictedBracket(); }, [poolTab, loadPredictedBracket]);

    // Show the welcome-bet popup ONCE per (user, pool) when a bet is defined
    // and the user is not the admin who set it up.
    //
    // Storage note: we persist a single "seen" flag in `localStorage` here.
    // The value is the literal string "1" — it contains no PII, tokens or
    // server-trusted state, so localStorage is the right tool (a UI
    // preference that should survive page reloads). Auth tokens are kept in
    // an httpOnly cookie set by the backend, never here.
    useEffect(() => {
        if (!pool || !user) return;
        const betDefined = (pool.bet_amount && pool.bet_amount > 0) || pool.bet_winner || pool.bet_instructions;
        if (!betDefined) return;
        if (pool.is_admin) return; // admin already knows
        const key = `vmtips_seen_bet_${pool.id}_${user.id}`;
        try {
            if (!localStorage.getItem(key)) {
                setShowWelcomeBet(true);
            }
        } catch (e) {
            // Privacy mode / disabled storage — fall back to never showing the
            // popup. Non-fatal.
            logger.warn("welcome-bet: localStorage read failed", e);
        }
    }, [pool, user]);

    const dismissWelcomeBet = useCallback(() => {
        setShowWelcomeBet(false);
        if (pool && user) {
            try {
                localStorage.setItem(`vmtips_seen_bet_${pool.id}_${user.id}`, "1");
            } catch (e) {
                // Privacy mode — the popup will re-show next visit, which is
                // acceptable behaviour for a non-sensitive UI flag.
                logger.warn("welcome-bet: localStorage write failed", e);
            }
        }
    }, [pool, user]);

    const openBetEdit = () => {
        setBetForm({
            bet_amount: pool.bet_amount != null ? String(pool.bet_amount) : "",
            bet_currency: pool.bet_currency || "",
            bet_winner: pool.bet_winner || "",
            bet_instructions: pool.bet_instructions || "",
        });
        setShowBetEdit(true);
    };

    const saveBet = async (e) => {
        e?.preventDefault?.();
        setSavingBet(true);
        try {
            const parsedAmount = betForm.bet_amount.trim() === "" ? null : Number(betForm.bet_amount);
            const payload = {
                bet_amount: Number.isFinite(parsedAmount) ? parsedAmount : null,
                bet_currency: betForm.bet_currency.trim(),
                bet_winner: betForm.bet_winner.trim(),
                bet_instructions: betForm.bet_instructions.trim(),
            };
            await api.put(`/pools/${poolId}/bet`, payload);
            setPool((p) => ({ ...p, ...payload }));
            toast.success(t("pool.bet.toast.saved"));
            setShowBetEdit(false);
        } catch (e2) {
            toast.error(formatApiError(e2.response?.data?.detail) || e2.message);
        } finally {
            setSavingBet(false);
        }
    };

    const togglePaid = async (memberUserId, currentPaid, memberName) => {
        setTogglingPaid((prev) => ({ ...prev, [memberUserId]: true }));
        try {
            const next = !currentPaid;
            await api.post(`/pools/${poolId}/members/${memberUserId}/paid`, { paid: next });
            setPool((p) => ({
                ...p,
                my_paid: memberUserId === user?.id ? next : p.my_paid,
                members: p.members.map((m) => m.id === memberUserId ? { ...m, paid: next } : m),
            }));
            toast.success(t(next ? "pool.members.toast.paid" : "pool.members.toast.unpaid", { name: memberName }));
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setTogglingPaid((prev) => { const n = { ...prev }; delete n[memberUserId]; return n; });
        }
    };

    const requestRemoveMember = (memberId, memberName) => {
        setRemoveMemberCandidate({ id: memberId, name: memberName });
    };

    const confirmRemoveMember = async () => {
        if (!removeMemberCandidate) return;
        setRemovingMember(true);
        try {
            await api.delete(`/pools/${poolId}/members/${removeMemberCandidate.id}`);
            setPool((p) => ({ ...p, members: p.members.filter((m) => m.id !== removeMemberCandidate.id) }));
            toast.success(t("pool.members.toast.removed", { name: removeMemberCandidate.name }));
            setRemoveMemberCandidate(null);
            loadLeaderboard();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally {
            setRemovingMember(false);
        }
    };

    const confirmDeletePool = async () => {
        setDeletingPool(true);
        try {
            await api.delete(`/pools/${poolId}`);
            toast.success(t("pool.admin.delete.toast"));
            nav("/dashboard");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
            setDeletingPool(false);
        }
    };

    const savePrediction = async (matchId, home, away, penWinnerTeamId = null) => {
        try {
            const { data } = await api.post(`/pools/${poolId}/predictions`, {
                match_id: matchId, home_goals: home, away_goals: away,
                pen_winner_team_id: penWinnerTeamId,
            });
            setPredictions((prev) => ({ ...prev, [matchId]: data }));
            toast.success(t("pool.toast.pred_saved"));
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    const recalc = async () => {
        try {
            await api.post(`/pools/${poolId}/recalculate`);
            await loadLeaderboard();
            toast.success(t("pool.toast.recalc"));
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    const copyCode = () => {
        if (pool?.code) {
            navigator.clipboard.writeText(pool.code);
            toast.success(t("pool.toast.code_copied"));
        }
    };

    const copyLink = () => {
        const url = `${window.location.origin}/join/${pool.code}`;
        navigator.clipboard.writeText(url);
        toast.success(t("pool.toast.link_copied"));
    };

    const filteredMatches = useMemo(() => {
        let list = matches.slice();
        if (filter === "upcoming") list = list.filter((m) => !isLocked(m.kickoff));
        if (filter === "locked") list = list.filter((m) => isLocked(m.kickoff));
        if (search) {
            const q = search.toLowerCase();
            list = list.filter((m) => {
                const h = teams[m.home_team_id]?.name?.toLowerCase() || "";
                const a = teams[m.away_team_id]?.name?.toLowerCase() || "";
                const r = m.round?.toLowerCase() || "";
                return h.includes(q) || a.includes(q) || r.includes(q);
            });
        }
        return list;
    }, [matches, filter, search, teams]);

    const groupedByRound = useMemo(() => {
        const g = {};
        filteredMatches.forEach((m) => {
            const r = m.round || "Other";
            if (!g[r]) g[r] = [];
            g[r].push(m);
        });
        return Object.entries(g);
    }, [filteredMatches]);

    const visibleTabs = TABS.filter((tab) => !tab.adminOnly || pool?.is_admin);

    if (loading) {
        return <div className="flex items-center justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-[var(--accent-hex)]" /></div>;
    }
    if (!pool) return <div className="p-8 text-center text-[var(--text-secondary)]">404</div>;

    const playerCount = pool.members?.length || 0;
    const playersLabel = playerCount === 1 ? t("pool.players_one", { n: playerCount }) : t("pool.players_other", { n: playerCount });
    const hasBet = (pool.bet_amount && pool.bet_amount > 0) || pool.bet_winner || pool.bet_instructions;
    const stakeLabel = (pool.bet_amount && pool.bet_amount > 0)
        ? `${pool.bet_amount}${pool.bet_currency ? ` ${pool.bet_currency}` : ""}`
        : null;

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-testid="pool-page">
            {/* Header */}
            <div className="mb-6">
                <Link to="/dashboard" className="text-xs uppercase tracking-widest font-bold text-[var(--text-secondary)] hover:text-[var(--text-primary)]" data-testid="back-to-dash">{t("pool.back_dash")}</Link>
                <div className="mt-2 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-3xl sm:text-5xl font-extrabold uppercase tracking-tight text-[var(--text-primary)]" data-testid="pool-name">{pool.name}</h1>
                        {pool.total_prize > 0 && (
                            <div className="mt-1 text-xs uppercase tracking-widest font-extrabold text-amber-400" data-testid="pool-prize-pot">
                                {t("pool.prize_pot")}: {pool.total_prize}{pool.bet_currency ? ` ${pool.bet_currency}` : ""}
                            </div>
                        )}
                        {pool.description && <p className="text-sm text-[var(--text-secondary)] mt-1 max-w-2xl">{pool.description}</p>}
                        <div className="flex items-center gap-4 mt-3 text-xs uppercase tracking-widest font-bold text-[var(--text-muted)]">
                            <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" /> {playersLabel}</span>
                            {pool.is_admin && <span className="text-[var(--accent-hex)]">{t("pool.you_admin")}</span>}
                        </div>
                    </div>
                    <div className="flex items-end gap-2 self-start sm:self-end flex-wrap">
                        {/* Pool quick-switcher — only shown when the user has >1 pool.
                            Picks immediately navigate to /pools/{newId}. */}
                        {myPools.length > 1 && (
                            <div className="flex flex-col items-start">
                                <label htmlFor="pool-quick-switch" className="text-[9px] uppercase tracking-widest font-bold text-[var(--text-secondary)] mb-1">
                                    {t("pool.quick_switch")}
                                </label>
                                <select
                                    id="pool-quick-switch"
                                    data-testid="pool-quick-switch"
                                    value={poolId}
                                    onChange={(e) => {
                                        if (e.target.value !== poolId) nav(`/pools/${e.target.value}`);
                                    }}
                                    className="bg-[var(--bg-card)] border border-[var(--border-base)] text-[var(--text-primary)] font-bold px-3 py-2 rounded-md text-sm focus:outline-none focus:border-[var(--accent-hex)] max-w-[200px] truncate"
                                >
                                    {myPools.map((p) => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                        <div className="flex items-center gap-2 p-3 rounded-lg border border-[var(--accent-border)] bg-[var(--accent-hex)]/5">
                            <div>
                                <div className="text-[9px] uppercase tracking-widest font-bold text-[var(--text-secondary)]">{t("pool.invite_code")}</div>
                                <div className="font-mono text-2xl font-extrabold text-[var(--accent-hex)] tracking-widest" data-testid="invite-code">{pool.code}</div>
                            </div>
                            <button onClick={copyCode} title={t("pool.copy_code")} data-testid="copy-code-btn"
                                className="h-9 w-9 rounded-md bg-[var(--bg-base)] hover:bg-[var(--bg-elev)] flex items-center justify-center text-[var(--accent-hex)]"><Copy className="h-4 w-4" /></button>
                            <button onClick={copyLink} title={t("pool.copy_link")} data-testid="copy-link-btn"
                                className="h-9 w-9 rounded-md bg-[var(--bg-base)] hover:bg-[var(--bg-elev)] flex items-center justify-center text-[var(--accent-hex)]"><Share2 className="h-4 w-4" /></button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-[var(--border-base)] mb-6 overflow-x-auto">
                <div className="flex gap-1 min-w-max">
                    {visibleTabs.map((tab) => {
                        const Icon = tab.icon;
                        const active = tab.id === poolTab;
                        return (
                            <button
                                key={tab.id}
                                data-testid={`tab-${tab.id}`}
                                onClick={() => setPoolTab(tab.id)}
                                className={`relative flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-widest font-bold transition-colors ${
                                    active ? "text-[var(--accent-hex)]" : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                                }`}
                            >
                                <Icon className="h-4 w-4" /> {t(tab.labelKey)}
                                {active && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--accent-hex)]" />}
                            </button>
                        );
                    })}
                </div>
            </div>

            {poolTab === "fixtures" && (
                <FixturesView
                    grouped={groupedByRound}
                    teams={teams}
                    predictions={predictions}
                    onSave={savePrediction}
                    filter={filter} setFilter={setFilter}
                    search={search} setSearch={setSearch}
                    standings={standings}
                    predictedBracket={predictedBracket}
                    poolId={poolId}
                    onAutoFilled={loadAll}
                    t={t}
                />
            )}

            {poolTab === "groups" && (
                <GroupsView standings={standings} teams={teams} t={t} poolId={poolId} />
            )}

            {poolTab === "bracket" && (
                <BracketView matches={matches} teams={teams} predictions={predictions} poolId={poolId} onPredictionsDeleted={loadAll} />
            )}

            {poolTab === "predictions" && (
                <MyPredictions teams={teams} poolId={poolId} />
            )}

            {poolTab === "bonus" && (
                <BonusTab poolId={poolId} teams={teams} isAdmin={pool.is_admin} t={t} onChange={loadLeaderboard} />
            )}

            {poolTab === "leaderboard" && (
                <Leaderboard rows={leaderboard} insights={insights} currentUserId={user?.id} onRefresh={loadLeaderboard} canRecalc={pool.is_admin} onRecalc={recalc} poolId={poolId} pool={pool} />
            )}

            {poolTab === "mvp" && (
                <MvpHistory poolId={poolId} currentUserId={user?.id} t={t} />
            )}

            {poolTab === "bet" && (
                <BetAndMembersCard
                    pool={pool}
                    user={user}
                    hasBet={hasBet}
                    stakeLabel={stakeLabel}
                    onEdit={openBetEdit}
                    togglingPaid={togglingPaid}
                    onTogglePaid={togglePaid}
                    onRemoveMember={requestRemoveMember}
                    t={t}
                />
            )}

            {poolTab === "admin" && pool.is_admin && (
                <AdminPanel
                    matches={matches}
                    teams={teams}
                    onReload={loadAll}
                    onRecalc={recalc}
                    onDeletePool={() => setShowDeletePool(true)}
                    t={t}
                />
            )}

            {showWelcomeBet && hasBet && (
                <WelcomeBetModal pool={pool} stakeLabel={stakeLabel} onDismiss={dismissWelcomeBet} t={t} />
            )}

            {showBetEdit && pool.is_admin && (
                <BetEditModal
                    betForm={betForm}
                    setBetForm={setBetForm}
                    onClose={() => setShowBetEdit(false)}
                    onSubmit={saveBet}
                    saving={savingBet}
                    t={t}
                />
            )}

            {showDeletePool && pool.is_admin && (
                <DeletePoolModal
                    poolName={pool.name}
                    deleting={deletingPool}
                    onClose={() => setShowDeletePool(false)}
                    onConfirm={confirmDeletePool}
                    t={t}
                />
            )}

            {removeMemberCandidate && pool.is_admin && (
                <RemoveMemberModal
                    candidate={removeMemberCandidate}
                    removing={removingMember}
                    onClose={() => setRemoveMemberCandidate(null)}
                    onConfirm={confirmRemoveMember}
                    t={t}
                />
            )}
        </div>
    );
}
